import React, { useEffect, useMemo, useState } from 'react';

import { fetchStaffList } from '../../services/api/staffApi';
import { formatStaffName } from '../../utils/staffName';
import { hopPaymentDueRupees } from '../../utils/visitMoney';

/**
 * Phase 3.1 + 3.1.5 — IncomingReferralBanner.
 *
 * Renders above the action panel on the visit screen when the visit
 * arrived at the current doctor via a referral. Reads from the most
 * recent `visit.referral_history` hop where `to_resolved` matches the
 * current viewer.
 *
 * Sections:
 *   - Header: "🟠 Referred from Dr A — reason"
 *   - Chips:  payment-collected status, chain length, journey toggle
 *   - Chain:  when multi-hop, "Dr A → Dr B → You"
 *   - Journey expander (3.1.5): per-hop list with timestamps, reasons,
 *     payment, who actioned the hop
 *
 * Intentionally read-only — Dr B's actions live in the existing
 * action panel + Rx panel below. No write paths.
 *
 * Props:
 *   visit      — visit row including `referral_history`
 *   viewerId   — current viewer's member_id
 *   onJumpToRx — optional () => void; scroll to prescription panel.
 */
function _formatTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

function _shortId(id) {
  if (!id) return '?';
  return `Dr ${String(id).slice(0, 6)}…`;
}

function _rupees(paise) {
  if (paise == null) return null;
  const n = Number(paise);
  if (!Number.isFinite(n)) return null;
  return `₹${(n / 100).toFixed(2)}`;
}

function _rupeesFromRupees(rupees) {
  if (rupees == null) return null;
  const n = Number(rupees);
  if (!Number.isFinite(n)) return null;
  return `₹${n.toFixed(2)}`;
}

export default function IncomingReferralBanner({ visit, viewerId, onJumpToRx }) {
  const history = useMemo(
    () => (Array.isArray(visit?.referral_history) ? visit.referral_history : []),
    [visit?.referral_history],
  );

  // Member-id → display name. Loaded lazily (banner only renders for
  // referred visits, which is a small fraction of visits).
  const [staffMap, setStaffMap] = useState(null);
  useEffect(() => {
    if (!history.length) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetchStaffList();
        if (cancelled || !r?.success) return;
        const m = new Map();
        for (const s of r.data || []) {
          const name = formatStaffName(s) || s.member_id;
          m.set(s.member_id, { name, role: s.role });
        }
        setStaffMap(m);
      } catch {
        /* noop — fall back to short ids */
      }
    })();
    return () => { cancelled = true; };
  }, [history.length]);

  const [journeyOpen, setJourneyOpen] = useState(false);

  if (!history.length) return null;

  // Most recent hop where this viewer is the receiving doctor.
  const incoming = [...history]
    .reverse()
    .find((h) => h?.to_resolved === viewerId);
  if (!incoming) return null;

  const resolveName = (id) => {
    if (!id) return null;
    const m = staffMap?.get(id);
    if (m?.name) return `Dr ${m.name}`;
    if (id === viewerId) return 'You';
    return _shortId(id);
  };

  const fromDoctorName = (
    visit?.referred_from_name
    || resolveName(incoming.from_doctor_id)
    || 'Dr. ?'
  );
  const reason = incoming.reason;
  const paymentCollected = (
    incoming.payment_collected_paise != null
    && Number(incoming.payment_collected_paise) > 0
  );
  const bypassed = !!incoming.bypassed_receptionist;

  // Chain labels: from-doctor → ... → you
  const chainLabels = (() => {
    if (history.length <= 1) return null;
    const labels = [];
    // First doctor (origin) = first hop's from_doctor_id.
    const first = history[0]?.from_doctor_id;
    if (first) labels.push(resolveName(first) || _shortId(first));
    for (const h of history) {
      labels.push(
        h.to_resolved === viewerId
          ? 'You'
          : (resolveName(h.to_resolved) || _shortId(h.to_resolved)),
      );
    }
    return labels;
  })();

  return (
    <div className="mb-3 rounded-md border border-orange-400 bg-orange-50 dark:border-orange-700 dark:bg-orange-900/30 px-3 py-2">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-orange-900 dark:text-orange-100">
            🟠 Referred from {fromDoctorName}
          </div>
          {reason && (
            <div className="mt-0.5 text-xs italic text-orange-900 dark:text-orange-100">
              “{reason}”
            </div>
          )}

          {/* Chain row when multi-hop */}
          {chainLabels && (
            <div className="mt-1 text-[11px] text-orange-900 dark:text-orange-100 flex items-center gap-1 flex-wrap">
              {chainLabels.map((name, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <span aria-hidden="true">→</span>}
                  <span className={name === 'You' ? 'font-semibold underline' : ''}>
                    {name}
                  </span>
                </React.Fragment>
              ))}
            </div>
          )}

          <div className="mt-1 flex items-center gap-2 flex-wrap text-[11px]">
            {paymentCollected ? (
              <span className="inline-flex items-center gap-1 rounded-full border border-emerald-400 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-900/40 px-2 py-0.5 text-emerald-900 dark:text-emerald-100">
                ✓ Payment collected ({_rupees(incoming.payment_collected_paise)})
              </span>
            ) : bypassed ? (
              <span className="inline-flex items-center gap-1 rounded-full border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-0.5 text-slate-700 dark:text-slate-200">
                Direct (no payment)
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 rounded-full border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-0.5 text-slate-700 dark:text-slate-200">
                Routed via Reception
              </span>
            )}
            <span className="inline-flex items-center gap-1 rounded-full border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-0.5 text-slate-700 dark:text-slate-200">
              Hop {history.length}
            </span>
            <button
              type="button"
              onClick={() => setJourneyOpen((x) => !x)}
              className="inline-flex items-center gap-1 rounded-full border border-orange-400 dark:border-orange-700 bg-white dark:bg-slate-800 px-2 py-0.5 text-orange-900 dark:text-orange-100 hover:bg-orange-50 dark:hover:bg-slate-700"
              aria-expanded={journeyOpen}
            >
              {journeyOpen ? 'Hide journey ↑' : 'Show journey ↓'}
            </button>
          </div>
        </div>

        {onJumpToRx && (
          <button
            type="button"
            onClick={onJumpToRx}
            className="px-3 py-1 text-xs font-medium rounded-md border border-orange-400 dark:border-orange-700 bg-white dark:bg-slate-800 text-orange-900 dark:text-orange-100 hover:bg-orange-50 dark:hover:bg-slate-700"
          >
            View prior prescriptions ↓
          </button>
        )}
      </div>

      {/* Journey expander — per-hop chronological list */}
      {journeyOpen && (
        <ol className="mt-2 pt-2 border-t border-orange-300 dark:border-orange-700 space-y-1.5">
          {history.map((hop, i) => {
            const fromName = resolveName(hop.from_doctor_id) || _shortId(hop.from_doctor_id);
            const toResolvedName = (
              hop.to_resolved === viewerId
                ? 'You'
                : (resolveName(hop.to_resolved) || _shortId(hop.to_resolved))
            );
            const overridden = (
              hop.to_suggested
              && hop.to_resolved
              && hop.to_suggested !== hop.to_resolved
            );
            const byName = resolveName(hop.by_member_id) || _shortId(hop.by_member_id);
            return (
              <li
                key={i}
                className="text-[11px] text-orange-900 dark:text-orange-100 leading-snug"
              >
                <strong>{i + 1}.</strong>{' '}
                <span>
                  {fromName} referred → <strong>{toResolvedName}</strong>
                </span>
                {overridden && (
                  <span className="ml-1 italic opacity-80">
                    (suggested {resolveName(hop.to_suggested) || _shortId(hop.to_suggested)})
                  </span>
                )}
                {hop.reason && (
                  <span className="block ml-4 italic opacity-90">“{hop.reason}”</span>
                )}
                <span className="block ml-4 opacity-80">
                  {hop.bypassed_receptionist
                    ? 'direct (no receptionist hop)'
                    : `forwarded by ${byName}`}
                  {hop.payment_collected_paise != null
                    && Number(hop.payment_collected_paise) > 0
                    && ` · collected ${_rupees(hop.payment_collected_paise)}`}
                  {/* Phase 3.5 — `payment_due` (rupees) replaced
                      `payment_due_paise` on new hops; helper falls back. */}
                  {hopPaymentDueRupees(hop) > 0
                    && (hop.payment_collected_paise == null
                      || Number(hop.payment_collected_paise) === 0)
                    && ` · due ${_rupeesFromRupees(hopPaymentDueRupees(hop))}`}
                  {hop.at && ` · ${_formatTime(hop.at)}`}
                </span>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
